// ============================================================================
// IssueMap ViewModel
// ============================================================================

import React, { useMemo } from "react";
import { useMapsLibrary, AdvancedMarker } from "@vis.gl/react-google-maps";
import { Polygon } from "../../../../../jobsdashboard/map";
import type { Point, Polygon as GeoJSONPolygon, MultiPolygon } from "geojson";
import type { IssueMapProps, IssueMapViewModel, FeatureMetadata } from "./IssueMap.types";
import { getJobBoundaryGeometry, extractPathsFromGeometry, getAllCoordinatesFromGeometry } from "./IssueMap.helpers";
import {
	BOUNDARY_COLORS,
	NEARBY_JOB_COLOR,
	HIGHLIGHT_COLOR,
	HIGHLIGHT_STROKE_COLOR,
	UNMATCHED_POINT_COLOR,
	UNMATCHED_ZONE_COLOR,
	DEFAULT_CENTER,
} from "../../shared/constants";

const SELECTED_COLOR = "#ff0000";
const SELECTED_OPACITY = 0.6;

/**
 * ViewModel hook for IssueMap component
 * Handles all business logic for processing jobs and generating map elements
 */
export function useIssueMapViewModel(props: IssueMapProps): IssueMapViewModel {
	const {
		jobs,
		highlightGeometry,
		nearbyJobs,
		hiddenJobIds,
		unmatchedFeatures,
		hideUnmatchedFeatures = false,
		hiddenNearbyJobIds,
		selectedFeatureIds,
		lassoMode,
	} = props;

	const mapsLibrary = useMapsLibrary("maps");

	// Process job boundaries into polygon elements
	const { polygonElements, markerElements, allCoordinates, featureMetadata } = useMemo(() => {
		const polygons: React.ReactElement[] = [];
		const markers: React.ReactElement[] = [];
		const coords: google.maps.LatLngLiteral[] = [];
		const metadata: FeatureMetadata[] = [];

		// Render main job boundaries
		jobs.forEach((job, index) => {
			// Skip hidden jobs but keep color index consistent
			const isHidden = hiddenJobIds?.includes(job.id);
			const boundaryGeom = getJobBoundaryGeometry(job);

			if (boundaryGeom) {
				const paths = extractPathsFromGeometry(boundaryGeom);
				const color = BOUNDARY_COLORS[index % BOUNDARY_COLORS.length];

				// Always collect coordinates for bounds (so map shows full extent)
				coords.push(...getAllCoordinatesFromGeometry(boundaryGeom));

				// Only render if not hidden
				if (!isHidden) {
					// Create polygon for each path
					paths.forEach((path, pathIndex) => {
						polygons.push(
							React.createElement(Polygon, {
								key: `job-${job.id}-${pathIndex}`,
								paths: path,
								fillColor: color,
								fillOpacity: 0.4,
								strokeColor: color,
								strokeWeight: 3,
								strokeOpacity: 1,
							}),
						);
					});
				}
			}
		});

		// Render nearby job boundaries (grayed out)
		nearbyJobs?.forEach((nearby) => {
			const isHidden = hiddenNearbyJobIds?.includes(nearby.job.id);
			const boundaryGeom = getJobBoundaryGeometry(nearby.job);

			if (boundaryGeom) {
				const paths = extractPathsFromGeometry(boundaryGeom);

				// Always collect coordinates for bounds (so map shows full extent)
				coords.push(...getAllCoordinatesFromGeometry(boundaryGeom));

				// Only render if not hidden
				if (!isHidden) {
					paths.forEach((path, pathIndex) => {
						polygons.push(
							React.createElement(Polygon, {
								key: `nearby-${nearby.job.id}-${pathIndex}`,
								paths: path,
								fillColor: NEARBY_JOB_COLOR,
								fillOpacity: 0.2,
								strokeColor: NEARBY_JOB_COLOR,
								strokeWeight: 2,
								strokeOpacity: 0.5,
							}),
						);
					});
				}
			}
		});

		// Render highlight geometry (e.g., intersection area)
		if (highlightGeometry?.geometry) {
			const geom = highlightGeometry.geometry;
			if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
				const paths = extractPathsFromGeometry(geom as GeoJSONPolygon | MultiPolygon);

				// Collect coordinates for bounds
				coords.push(...getAllCoordinatesFromGeometry(geom as GeoJSONPolygon | MultiPolygon));

				paths.forEach((path, pathIndex) => {
					polygons.push(
						React.createElement(Polygon, {
							key: `highlight-${pathIndex}`,
							paths: path,
							fillColor: HIGHLIGHT_COLOR,
							fillOpacity: 0.15,
							strokeColor: HIGHLIGHT_STROKE_COLOR,
							strokeWeight: 4,
							strokeOpacity: 0.9,
						}),
					);
				});
			}
		}

		// Render unmatched features (points and zones)
		if (unmatchedFeatures?.features) {
			unmatchedFeatures.features.forEach((feature, index) => {
				const geom = feature.geometry;
				if (!geom) return;

				if (geom.type === "Point") {
					const point = geom as Point;
					const position = { lat: point.coordinates[1], lng: point.coordinates[0] };
					const featureId = `unmatched-point-${index}`;
					const isSelected = selectedFeatureIds?.has(featureId);

					// Always collect coordinates for bounds
					coords.push(position);
					// Track metadata for lasso selection
					metadata.push({ id: featureId, paths: [position] });

					// Only render if not hidden
					if (!hideUnmatchedFeatures) {
						markers.push(
							React.createElement(
								AdvancedMarker,
								{ key: featureId, position },
								React.createElement("div", {
									style: {
										width: 14,
										height: 14,
										backgroundColor: isSelected ? SELECTED_COLOR : UNMATCHED_POINT_COLOR,
										borderRadius: "50%",
										border: isSelected ? "3px solid white" : "2px solid white",
										boxShadow: isSelected ? "0 2px 8px rgba(255,0,0,0.5)" : "0 2px 4px rgba(0,0,0,0.3)",
									},
								}),
							),
						);
					}
				} else if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
					const paths = extractPathsFromGeometry(geom as GeoJSONPolygon | MultiPolygon);
					// Always collect coordinates for bounds
					coords.push(...getAllCoordinatesFromGeometry(geom as GeoJSONPolygon | MultiPolygon));

					// Only render if not hidden
					if (!hideUnmatchedFeatures) {
						paths.forEach((path, pathIndex) => {
							const featureId = `unmatched-zone-${index}-${pathIndex}`;
							const isSelected = selectedFeatureIds?.has(featureId);

							// Track metadata for lasso selection
							metadata.push({ id: featureId, paths: path });

							polygons.push(
								React.createElement(Polygon, {
									key: featureId,
									paths: path,
									fillColor: isSelected ? SELECTED_COLOR : UNMATCHED_ZONE_COLOR,
									fillOpacity: isSelected ? SELECTED_OPACITY : 0.4,
									strokeColor: isSelected ? SELECTED_COLOR : UNMATCHED_ZONE_COLOR,
									strokeWeight: isSelected ? 4 : 3,
									strokeOpacity: 1,
									clickable: !lassoMode,
								}),
							);
						});
					}
				}
			});
		}

		return { polygonElements: polygons, markerElements: markers, allCoordinates: coords, featureMetadata: metadata };
	}, [
		jobs,
		nearbyJobs,
		highlightGeometry,
		hiddenJobIds,
		unmatchedFeatures,
		hideUnmatchedFeatures,
		hiddenNearbyJobIds,
		selectedFeatureIds,
		lassoMode,
	]);

	// Calculate center from coordinates
	const center = useMemo(() => {
		if (allCoordinates.length === 0) return DEFAULT_CENTER;

		const sumLat = allCoordinates.reduce((sum, c) => sum + c.lat, 0);
		const sumLng = allCoordinates.reduce((sum, c) => sum + c.lng, 0);

		return {
			lat: sumLat / allCoordinates.length,
			lng: sumLng / allCoordinates.length,
		};
	}, [allCoordinates]);

	return {
		polygonElements,
		markerElements,
		allCoordinates,
		center,
		isLoading: !mapsLibrary,
		featureMetadata,
	};
}
